<template>
  <div class="search-bar">
    <div class="search-bar__select">
      <v-select
        v-model="column_name"
        :items="columns"
        item-text="title"
        item-value="field"
        placeholder="Column"
        solo
        dense
        flat
        hide-details
        @change="changeSearchType"
        class="search-select"
      ></v-select>
    </div>
    <div class="search-bar__input">
      <v-icon size="18" color="var(--app-text-faint)" class="search-bar__icon">mdi-magnify</v-icon>
      <v-text-field
        v-model="value"
        placeholder="Search..."
        :type="this.valueInputType"
        solo
        dense
        flat
        clearable
        hide-details
        @click:clear="clearSearch"
        @keyup.native.enter="searchRecords"
        class="search-input"
      ></v-text-field>
    </div>
    <button
      class="search-bar__btn search-bar__btn--search"
      :disabled="!value"
      @click="searchRecords"
    >
      <v-icon size="16" class="mr-1">mdi-magnify</v-icon>
      Search
    </button>
    <button
      class="search-bar__btn search-bar__btn--clear"
      :disabled="!value && selectedFilters.length === 0"
      @click="clearSearch()"
    >
      <v-icon size="16" class="mr-1">mdi-close-circle-outline</v-icon>
      Clear
    </button>
  </div>
</template>

<script>
export default {
  props: {
    model: {
      type: Object,
      required: true,
    },
    setConditions: {
      type: Function,
    },
  },
  data: () => ({
    column_name: null,
    columns: [],
    filters: [],
    type: null,
    selectedFilters: [],
    value: null,
    valueInputType: "text",
    selectedViewFilteredData: [],
  }),
  watch: {
    selectedFilters() {
      this.updateConditions();
    },
  },
  created() {
    this.columns = this.$_.filter(this.model.browse.table.headers, (o) => {
      if (o.searchable != false) {
        return o;
      }
    });
    this.column_name = this.columns[0] ? this.columns[0].field : null;
    this.type = "like";

    const searchDataStore = this.$store.state[this.model.serviceKey]?.searchData;
    if (searchDataStore) {
      // Get search value from store.
      this.value = searchDataStore.value;
      this.column_name = searchDataStore.field;
      this.searchRecords();
    }
  },
  methods: {
    searchRecords() {
      this.selectedFilters = [];

      var selectedColumn = this.$_.find(this.columns, {
        field: this.column_name,
      });

      switch (selectedColumn.dataType) {
        case "float":
          this.value = parseFloat(this.value);
          break;
        case "integer":
          this.value = parseInt(this.value, 10);
          break;
        case "boolean":
          this.value = this.value == "true" ? true : false;
          break;

        case "date":
          this.value = this.$moment(new Date(this.value)).format("YYYY-MM-DD");
          break;
      }

      let condition = {
        field: selectedColumn.field,
        type: this.type,
        value:
          selectedColumn.dataType == "date" ? new Date(this.value) : this.value,
      };

      this.selectedFilters.push(JSON.stringify(condition));
    },
    clearSearch() {
      this.selectedFilters = [];
      this.value = null;
    },
    updateConditions() {
      var conditions = [];
      for (var condition of this.selectedFilters) {
        conditions.push(JSON.parse(condition));
      }

      if (this.selectedViewFilteredData.length > 0) {
        conditions = conditions.concat(this.selectedViewFilteredData);
      }

      this.setConditions(conditions);
    },
    resetCondition() {
      this.value = null;
    },
    changeSearchType() {
      this.resetCondition();

      var selectedColumn = this.$_.find(this.columns, {
        field: this.column_name,
      });

      switch (selectedColumn.dataType) {
        case "float":
          this.type = "=";
          this.valueInputType = "number";
          break;
        case "integer":
          this.type = "=";
          this.valueInputType = "number";
          break;
        case "boolean":
          this.type = "=";
          this.valueInputType = "text";
          break;
        case "date":
          this.type = "=";
          this.valueInputType = "date";
          break;

        default:
          this.type = "like";
          this.valueInputType = "text";
          break;
      }
    },
    getFilterCondition(filterCondition) {
      this.selectedViewFilteredData = filterCondition;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .search-bar__btn--clear {
  background: var(--app-card);
  color: var(--app-text-muted);
  border: 1.5px solid var(--app-border-strong);
}

.dark-mode .search-bar__btn--clear:hover:not(:disabled) {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .search-select >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1px solid var(--app-field-border) !important;
}

.dark-mode .search-select >>> .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .search-select >>> .v-select__selection {
  color: var(--app-text) !important;
}

.dark-mode .search-select >>> .v-label {
  color: var(--app-field-placeholder) !important;
}

.dark-mode .search-input >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1px solid var(--app-field-border) !important;
}

.dark-mode .search-input >>> .v-input__slot:hover {
  border-color: var(--app-field-border-hover) !important;
}

/* The light rule pins the typed value to a near-black hex with !important; with
   no dark companion it rendered 1.23:1 on the dark field — the text was there,
   just invisible unless you selected it. */
.dark-mode .search-input >>> .v-text-field__slot input {
  color: var(--app-text) !important;
}

.dark-mode .search-input >>> .v-text-field__slot input::placeholder {
  color: var(--app-field-placeholder) !important;
}

/* >>> end auto-dark <<< */

.search-bar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.search-bar__select {
  width: 160px;
}

.search-bar__input {
  width: 200px;
  position: relative;
  display: flex;
  align-items: center;
}

.search-bar__icon {
  position: absolute;
  left: 10px;
  z-index: 1;
  pointer-events: none;
}

/* Buttons */
.search-bar__btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.search-bar__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.search-bar__btn--search {
  background: #0ea5e9;
  color: #fff;
}

.search-bar__btn--search:hover:not(:disabled) {
  background: #0284c7;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.search-bar__btn--clear {
  background: #fff;
  color: #64748b;
  border: 1.5px solid #d1d5db;
}

.search-bar__btn--clear:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Select overrides */
.search-select >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e4e8 !important;
  border-radius: 10px !important;
  min-height: 38px !important;
  box-shadow: none !important;
  padding: 0 12px !important;
}

.search-select >>> .v-input__slot::before,
.search-select >>> .v-input__slot::after {
  display: none !important;
}

.search-select >>> .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
}

.search-select >>> .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  color: #94a3b8 !important;
}

.search-select >>> .v-input__slot:hover {
  border-color: #c9cdd3 !important;
}

/* Text input overrides */
.search-input >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e4e8 !important;
  border-radius: 10px !important;
  min-height: 38px !important;
  box-shadow: none !important;
  padding: 0 12px 0 34px !important;
}

.search-input >>> .v-input__slot::before,
.search-input >>> .v-input__slot::after {
  display: none !important;
}

.search-input >>> .v-text-field__slot input {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

.search-input >>> .v-text-field__slot input::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

.search-input >>> .v-input__slot:hover {
  border-color: #c9cdd3 !important;
}

.search-input >>> .v-input__append-inner {
  margin-top: 7px !important;
}

/* Responsive */
@media (max-width: 959px) {
  .search-bar {
    display: flex;
    width: 100%;
  }

  .search-bar__select {
    width: 100%;
  }

  .search-bar__input {
    width: 100%;
    flex: 1;
  }

  .search-bar__btn {
    padding: 8px 12px;
    font-size: 0.78rem;
  }
}
</style>

<style>
/* The app-wide dropdown-menu styling that used to live here has moved to
   src/plugins/theme-glass.css (§3).

   It was never really this component's: the block was NOT scoped, so a bare
   `.v-menu__content .v-list { background: #fff !important }` written for the
   table's column picker was painting every v-select, autocomplete and date
   picker in the app — and, being !important, it outranked anything the shared
   layer could say. It also carried the usual split, with a `.dark-mode` copy of
   each rule that had to be kept in step by hand.

   The glass layer states those rules once, in tokens, at `#app …` specificity
   and without !important, so a menu that needs to differ (the navbar dropdowns,
   the table's own tb-view-menu) can still override them. */
</style>
